// src/data/disciplineIntake.js
//
// RULING 308 AT INTAKE (Bob 2026-10-14: "Discipline intake per ruling 308 ... address these immediately, properly,
// accurately, and securely"). Three meetings, three records: the INTENT meeting (Amtrak Rule 36(a)), the prehearing
// meeting, the hearing. At intake that means:
//   1. The letter of intent and the charging letter are DIFFERENT letters - separate slots, separate dates.
//   2. RECEIPT is a fact recorded, never assumed from a letter's date (308.4). The intent meeting runs from receipt:
//      "Within seven days from receipt of the written intent to discipline ..." (amtrak-ym-2000-11-01.txt:894).
//   3. Where the agreement runs the hearing from the supervisor's FIRST KNOWLEDGE (Amtrak Rule 36(c) :909-910, CSX
//      Art. 21, Conrail Rule 6-A-1(b)), the deadline is counted from that date when it is recorded. When it is not, the
//      date shown is counted from the letter's date and SAID to be a latest bound: first knowledge cannot come after the
//      letter, so the true deadline is on or before it. Never a later date than the agreement allows.
//   4. The Amtrak letter-of-intent bar ("shall not be issued ... for any offense of which the employee's supervisor has
//      had actual knowledge for more than 30 calendar days", :889-893) is FLAGGED when both dates are recorded. Whether
//      it is a defect - the civil/criminal exception, the facts - is the officer's call, not the platform's.
// PURE: no React, no Firestore. Proved by scripts\test-discipline-intake.mjs.

import { lastDayOf, dayNumber } from './timeLimit';   // ruling 314: the first day counts as day one

const s = (v) => (v === undefined || v === null ? '' : String(v).trim());
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(s(v));

// `limits` is disciplineAnswerLimits.screenDeadlines(agreement) - or null when the agreement is not catalogued.
export function intakeClocks(form, limits) {
  const f = form || {};
  const L = limits || null;
  const out = {
    intentMeetingDue: null, intentMeetingNote: '',
    investigationDeadline: null, investigationDeadlineBasis: '', investigationDeadlineNote: '',
    intentBarFlag: '',
  };
  if (!L) return out;

  // 1-2. The intent meeting, from RECEIPT of the letter of intent.
  if (Number.isFinite(L.intentMeetingDays) && (isDate(f.intentLetterDate) || isDate(f.intentReceivedOn))) {
    if (isDate(f.intentReceivedOn)) {
      out.intentMeetingDue = lastDayOf(s(f.intentReceivedOn), L.intentMeetingDays);
      out.intentMeetingNote = 'The intent meeting is due within ' + L.intentMeetingDays + ' days from the employee\'s receipt of the letter of intent (received ' + s(f.intentReceivedOn) + ')'
        + (L.cites && L.cites.intentMeeting ? ' - ' + L.cites.intentMeeting : '') + '. If management\'s representative fails to attend, the letter is withdrawn and canceled.';
    } else {
      out.intentMeetingNote = 'No intent-meeting date is computed: the date the employee RECEIVED the letter of intent is not recorded. The clock runs from receipt, never from the letter\'s date.';
    }
  }

  // 3. The hearing deadline.
  const hearingDays = Number.isFinite(L.hearingDays) ? L.hearingDays : null;
  if (L.hearingFrom === 'first_knowledge' && hearingDays !== null && isDate(f.firstKnowledgeOn)) {
    out.investigationDeadline = lastDayOf(s(f.firstKnowledgeOn), hearingDays);
    out.investigationDeadlineBasis = 'first_knowledge';
    out.investigationDeadlineNote = 'The hearing must begin within ' + hearingDays + ' days of the supervisor\'s first knowledge (recorded as ' + s(f.firstKnowledgeOn) + ')'
      + (L.cites && L.cites.hearing ? ' - ' + L.cites.hearing : '') + '.';
  } else if (Number.isFinite(L.noticeDays) && isDate(f.noticeDate)) {
    out.investigationDeadline = lastDayOf(s(f.noticeDate), L.noticeDays);
    out.investigationDeadlineBasis = 'notice_date';
    out.investigationDeadlineNote = L.hearingFrom === 'first_knowledge'
      ? 'Counted from the date on the notice of charge, because the supervisor\'s first knowledge is not recorded. The agreement counts the hearing from first knowledge, which cannot be after the letter - so the true deadline is ON OR BEFORE this date. Record the first-knowledge date when the letter or the record shows it.'
      : 'Counted from the date on the notice of charge (' + L.noticeDays + ' days).';
  } else if (L.hearingFrom === 'first_knowledge' && hearingDays !== null) {
    out.investigationDeadlineNote = 'No hearing deadline is computed: neither the supervisor\'s first knowledge nor the date on a notice of charge is recorded.';
  }

  // 4. The letter-of-intent bar - measured on the day the letter reached the employee where that is recorded (ruling
  // 314: timeliness is receipt; Battersbee: dated March 1, hand delivered March 2 - day 31 of 30), else on its date.
  const issuedOn = isDate(f.intentReceivedOn) ? s(f.intentReceivedOn) : (isDate(f.intentLetterDate) ? s(f.intentLetterDate) : '');
  if (Number.isFinite(L.noticeOfIntentDays) && issuedOn && isDate(f.firstKnowledgeOn)) {
    const n = dayNumber(s(f.firstKnowledgeOn), issuedOn);
    if (n > L.noticeOfIntentDays) {
      out.intentBarFlag = 'The letter of intent ' + (isDate(f.intentReceivedOn) ? 'reached the employee' : 'is dated') + ' on day ' + n
        + ' counting the supervisor\'s recorded first knowledge (' + s(f.firstKnowledgeOn) + ') as day one; the agreement bars a letter of intent after '
        + L.noticeOfIntentDays + ' calendar days' + (L.cites && L.cites.noticeOfIntent ? ' (' + L.cites.noticeOfIntent + ')' : '')
        + ', except where a civil action or criminal proceeding results from the offense. Whether that is a defect here is the officer\'s call.';
    }
  }
  return out;
}

// What the intake must have before a case is opened: the employee, the type of discipline, and the date of at least
// ONE letter - the notice of charge or the letter of intent. A case may begin with a letter of intent (Rule 36(a))
// before any charge is preferred; demanding a charge date then would make the officer invent one.
export function intakeMissing(form) {
  const f = form || {};
  return [
    !s(f.memberName) ? 'the employee name' : '',
    !s(f.disciplineType) ? 'the type of discipline' : '',
    !isDate(f.noticeDate) && !isDate(f.intentLetterDate) ? 'the date on the notice of charge or on the letter of intent' : '',
  ].filter(Boolean);
}

// A receipt date before the letter's own date is a typing error, not a fact; the screen says so before saving.
export function receiptProblems(form) {
  const f = form || {};
  const out = [];
  if (isDate(f.intentLetterDate) && isDate(f.intentReceivedOn) && f.intentReceivedOn < f.intentLetterDate) out.push('The letter of intent is recorded as received before the date on it.');
  if (isDate(f.noticeDate) && isDate(f.chargeReceivedOn) && f.chargeReceivedOn < f.noticeDate) out.push('The notice of charge is recorded as received before the date on it.');
  if (isDate(f.firstKnowledgeOn) && isDate(f.intentLetterDate) && f.intentLetterDate < f.firstKnowledgeOn) out.push('The letter of intent is dated before the supervisor\'s recorded first knowledge.');
  return out;
}
